/**
 * useNavigationTabs Hook - Browser-Style Navigation Tabs
 *
 * Custom hook that provides browser-style tab navigation state and actions.
 * Uses atomic Zustand selectors for optimal performance (minimal re-renders).
 *
 * @example
 * ```tsx
 * const { tabs, activeTabId, openTab, closeTab } = useNavigationTabs();
 *
 * // Navigate to related task
 * openTab(taskId, taskTitle);
 *
 * // Switch to existing tab
 * switchTab(tabId);
 * ```
 *
 * Performance:
 * - 2 atomic subscriptions (tabs array, activeTabId) - re-render only when changed
 * - 6 action subscriptions (stable references) - never cause re-renders
 * - Actions don't change between renders (Zustand guarantee)
 */

import { useGeoMarkupStore } from '../store';

/**
 * Custom hook for browser-style navigation tabs
 * Follows pattern from useTaskActions and useUiState
 */
export const useNavigationTabs = () => {
  // ===== STATE SUBSCRIPTIONS =====
  // Subscribe to primitive/array values only
  // Each subscription re-renders ONLY when its specific value changes

  const tabs = useGeoMarkupStore((s) => s.taskNavigation.tabs);
  const activeTabId = useGeoMarkupStore((s) => s.taskNavigation.activeTabId);

  // ===== ACTIONS =====
  // Stable function references - never cause re-renders
  // Each action subscribed separately (no useShallow needed)

  const openTab = useGeoMarkupStore((s) => s.taskNavigation.openTab);
  const closeTab = useGeoMarkupStore((s) => s.taskNavigation.closeTab);
  const switchTab = useGeoMarkupStore((s) => s.taskNavigation.switchTab);
  const closeAllTabs = useGeoMarkupStore((s) => s.taskNavigation.closeAllTabs);
  const getTabByTaskId = useGeoMarkupStore(
    (s) => s.taskNavigation.getTabByTaskId
  );
  const updateTabTitle = useGeoMarkupStore(
    (s) => s.taskNavigation.updateTabTitle
  );

  return {
    // State (primitives/arrays)
    tabs,
    activeTabId,

    // Actions
    openTab,
    closeTab,
    switchTab,
    closeAllTabs,
    getTabByTaskId,
    updateTabTitle,
  };
};
